import { clsx } from 'clsx';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  BarChart3,
  Activity,
  HelpCircle,
} from 'lucide-react';

// Metric indicator status
type MetricStatus = 'good' | 'warning' | 'bad';

function getStatusColor(status: MetricStatus): string {
  switch (status) {
    case 'good':
      return 'text-green-600 bg-green-50 border-green-200';
    case 'warning':
      return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'bad':
      return 'text-red-600 bg-red-50 border-red-200';
  }
}

function getStatusIcon(status: MetricStatus) {
  switch (status) {
    case 'good':
      return CheckCircle;
    case 'warning':
      return AlertTriangle;
    case 'bad':
      return XCircle;
  }
}

function getStatusLabel(status: MetricStatus): string {
  switch (status) {
    case 'good':
      return 'Consistent';
    case 'warning':
      return 'Varies';
    case 'bad':
      return 'Inconsistent';
  }
}

interface MetricCardProps {
  label: string;
  value: string | number;
  suffix?: string;
  status: MetricStatus;
  description?: string;
  showStatusLabel?: boolean;
  tooltip?: string;
}

function MetricCard({ label, value, suffix = '', status, description, showStatusLabel = true, tooltip }: MetricCardProps) {
  const StatusIcon = getStatusIcon(status);

  return (
    <div className={clsx('rounded-lg border p-4', getStatusColor(status))}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide opacity-75 flex items-center gap-1">
            {label}
            {tooltip && (
              <span className="group relative cursor-help">
                <HelpCircle className="h-3.5 w-3.5 opacity-60 hover:opacity-100" />
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs font-normal normal-case tracking-normal bg-gray-900 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-56 text-left z-10 shadow-lg">
                  {tooltip}
                  <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                </span>
              </span>
            )}
          </p>
          <p className="mt-1 text-2xl font-bold">
            {typeof value === 'number' ? value.toFixed(value % 1 === 0 ? 0 : 1) : value}
            {suffix && <span className="text-lg font-normal opacity-75">{suffix}</span>}
          </p>
          {description && <p className="mt-1 text-xs opacity-75">{description}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusIcon className="h-5 w-5" />
          {showStatusLabel && (
            <span className="text-xs font-medium">{getStatusLabel(status)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

interface Phase1Metrics {
  suitabilityAgreement: number;
  suitabilityValues: string[];
  confidenceVariance: number;
  confidenceRange: { min: number; max: number };
  confidenceValues: number[];
  issueOverlap: number;
  criteriaConsistency: number;
  criteriaValueMatch: number;
  totalIterations: number;
}

interface Phase2Metrics {
  providerOverlap: number;
  top3Consistency: number;
  top5Consistency: number;
  rankingCorrelation: number;
  scoreVariance: number;
  uniqueProviders: number;
  commonProviders: number;
  totalIterations: number;
  // New fields to track empty iterations
  iterationsWithResults?: number;
  emptyIterations?: number;
}

interface Phase1ConsistencyMetricsProps {
  metrics: Phase1Metrics;
  showHeader?: boolean;
}

export function Phase1ConsistencyMetrics({ metrics, showHeader = true }: Phase1ConsistencyMetricsProps) {
  // Determine status based on thresholds
  const getSuitabilityStatus = (agreement: number): MetricStatus => {
    if (agreement === 100) return 'good';
    if (agreement >= 80) return 'warning';
    return 'bad';
  };

  const getConfidenceStatus = (variance: number): MetricStatus => {
    if (variance <= 5) return 'good';
    if (variance <= 15) return 'warning';
    return 'bad';
  };

  const getIssueStatus = (overlap: number): MetricStatus => {
    if (overlap >= 80) return 'good';
    if (overlap >= 50) return 'warning';
    return 'bad';
  };

  const getCriteriaStatus = (consistency: number): MetricStatus => {
    if (consistency >= 90) return 'good';
    if (consistency >= 70) return 'warning';
    return 'bad';
  };

  const confidenceRange = metrics.confidenceRange.max - metrics.confidenceRange.min;

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Consistency Summary</h3>
          <span className="text-sm text-gray-500">({metrics.totalIterations} iterations)</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Suitability"
          value={metrics.suitabilityAgreement}
          suffix="%"
          status={getSuitabilityStatus(metrics.suitabilityAgreement)}
          description={
            metrics.suitabilityAgreement === 100
              ? `All runs: ${metrics.suitabilityValues[0]}`
              : `Values: ${[...new Set(metrics.suitabilityValues)].join(', ')}`
          }
        />

        <MetricCard
          label="Confidence"
          value={confidenceRange}
          suffix="% range"
          status={getConfidenceStatus(confidenceRange)}
          description={`${metrics.confidenceRange.min}% - ${metrics.confidenceRange.max}%`}
        />

        <MetricCard
          label="Issue Overlap"
          value={metrics.issueOverlap}
          suffix="%"
          status={getIssueStatus(metrics.issueOverlap)}
          description="Same issues found across runs"
        />

        <MetricCard
          label="Criteria Match"
          value={metrics.criteriaConsistency}
          suffix="%"
          status={getCriteriaStatus(metrics.criteriaConsistency)}
          description={`${metrics.criteriaValueMatch}% with same values`}
        />
      </div>

      {/* Overall assessment */}
      <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-gray-600" />
          <span className="font-medium text-gray-900">Overall Assessment:</span>
          {metrics.suitabilityAgreement === 100 &&
           confidenceRange <= 10 &&
           metrics.criteriaConsistency >= 85 ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
              <CheckCircle className="h-4 w-4" />
              Highly Consistent
            </span>
          ) : metrics.suitabilityAgreement >= 80 &&
                confidenceRange <= 20 &&
                metrics.criteriaConsistency >= 70 ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              Moderately Consistent
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
              <XCircle className="h-4 w-4" />
              Inconsistent
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface Phase2ConsistencyMetricsProps {
  metrics: Phase2Metrics;
}

export function Phase2ConsistencyMetrics({ metrics }: Phase2ConsistencyMetricsProps) {
  const hasEmptyIterations = (metrics.emptyIterations ?? 0) > 0;
  const iterationsWithResults = metrics.iterationsWithResults ?? metrics.totalIterations;

  const getOverlapStatus = (overlap: number): MetricStatus => {
    if (overlap >= 80) return 'good';
    if (overlap >= 50) return 'warning';
    return 'bad';
  };

  const getTop3Status = (consistency: number): MetricStatus => {
    if (consistency >= 90) return 'good';
    if (consistency >= 60) return 'warning';
    return 'bad';
  };

  const getCorrelationStatus = (correlation: number): MetricStatus => {
    if (correlation >= 0.8) return 'good';
    if (correlation >= 0.5) return 'warning';
    return 'bad';
  };

  // Determine overall assessment considering empty iterations
  const getOverallAssessment = () => {
    // If there are empty iterations, it's a sign of instability
    if (hasEmptyIterations) {
      // If only 1 iteration has results, we can't assess consistency
      if (iterationsWithResults <= 1) {
        return {
          status: 'bad' as const,
          label: 'Cannot Assess',
          description: `${metrics.emptyIterations} of ${metrics.totalIterations} iterations returned no results`,
        };
      }
      // Penalize assessment for having empty iterations
      return {
        status: 'warning' as const,
        label: 'Partial Results',
        description: `${metrics.emptyIterations} iteration(s) returned no results`,
      };
    }

    // Normal assessment based on metrics
    if (metrics.top3Consistency >= 90 && metrics.providerOverlap >= 70) {
      return { status: 'good' as const, label: 'Highly Consistent', description: null };
    }
    if (metrics.top3Consistency >= 60 && metrics.providerOverlap >= 50) {
      return { status: 'warning' as const, label: 'Moderately Consistent', description: null };
    }
    return { status: 'bad' as const, label: 'Inconsistent', description: null };
  };

  const assessment = getOverallAssessment();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Activity className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Provider Consistency</h3>
        <span className="text-sm text-gray-500">({metrics.totalIterations} iterations)</span>
      </div>

      {/* Warning banner for empty iterations */}
      {hasEmptyIterations && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {metrics.emptyIterations} of {metrics.totalIterations} iterations returned no results
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Metrics below are calculated from the {iterationsWithResults} iteration(s) that have results.
                Empty iterations indicate potential instability in the search criteria.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Provider Overlap"
          value={metrics.providerOverlap}
          suffix="%"
          status={getOverlapStatus(metrics.providerOverlap)}
          description={`${metrics.commonProviders}/${metrics.uniqueProviders} common`}
        />

        <MetricCard
          label="Top-3 Match"
          value={metrics.top3Consistency}
          suffix="%"
          status={getTop3Status(metrics.top3Consistency)}
          description="Same top 3 providers"
        />

        <MetricCard
          label="Top-5 Match"
          value={metrics.top5Consistency}
          suffix="%"
          status={getTop3Status(metrics.top5Consistency)}
          description="Same top 5 providers"
        />

        <MetricCard
          label="Ranking Correlation"
          value={metrics.rankingCorrelation}
          status={getCorrelationStatus(metrics.rankingCorrelation)}
          description="Kendall's tau"
          tooltip="Kendall's tau measures how consistently providers are ranked across iterations. A value of 1 means identical rankings, 0 means no correlation, and -1 means opposite rankings."
        />
      </div>

      {/* Overall assessment */}
      <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
        <div className="flex items-center gap-2 flex-wrap">
          <BarChart3 className="h-5 w-5 text-gray-600" />
          <span className="font-medium text-gray-900">Overall Assessment:</span>
          {assessment.status === 'good' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
              <CheckCircle className="h-4 w-4" />
              {assessment.label}
            </span>
          ) : assessment.status === 'warning' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
              <AlertTriangle className="h-4 w-4" />
              {assessment.label}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
              <XCircle className="h-4 w-4" />
              {assessment.label}
            </span>
          )}
          {assessment.description && (
            <span className="text-sm text-gray-500">- {assessment.description}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Export types for use in other components
export type { Phase1Metrics, Phase2Metrics };
